import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { StaffRail } from '@/components/layout/StaffRail';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Send, Cake, LogOut, Users, MessageSquare, RefreshCw, Ticket, Plus, Copy, Crown,
  Clock4, Sparkles, BadgePercent, DollarSign, Truck,
  AlertCircle, CheckCircle2, Ban,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { fmtTimeAgo, fmtMoney } from '@/lib/realtime';

const SEGMENTS = [
  { key: 'all_customers',     label: 'All customers',      icon: Users,     tone: 'text-foreground',   hint: 'Every phone number seen in an order.' },
  { key: 'vip_gold_platinum', label: 'VIP',                icon: Crown,     tone: 'text-amber-700',    hint: 'Manually flagged OR 10+ orders.' },
  { key: 'inactive_30_days',  label: 'Inactive (30 d)',    icon: Clock4,    tone: 'text-rose-700',     hint: 'Last order > 30 days ago.' },
  { key: 'recent_customers',  label: 'Recent (7 d)',       icon: Sparkles,  tone: 'text-emerald-700',  hint: 'Ordered in the past week.' },
  { key: 'birthdays_today',   label: 'Birthday today',     icon: Cake,      tone: 'text-pink-700',     hint: 'Date of birth matches today.' },
];

export default function MarketingPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [segment, setSegment] = useState('all_customers');
  const [counts, setCounts] = useState({});
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const [sending, setSending] = useState(false);

  const loadSummary = useCallback(async () => {
    try { const { data } = await api.get('/marketing/segments/summary'); setCounts(data || {}); } catch (e) { /* */ }
  }, []);
  const loadBroadcasts = useCallback(async () => {
    try { const { data } = await api.get('/marketing/broadcasts'); setBroadcasts(data || []); } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { loadSummary(); loadBroadcasts(); }, [loadSummary, loadBroadcasts]);

  const previewSeg = useCallback(async () => {
    try {
      const { data } = await api.get(`/marketing/segments/${segment}/preview`);
      setPreview(data);
    } catch (e) { toast.error('Failed to load segment'); }
  }, [segment]);
  useEffect(() => { previewSeg(); }, [previewSeg]);

  const broadcast = async () => {
    if (message.length < 4) { toast.error('Message too short'); return; }
    setSending(true);
    try {
      const { data } = await api.post('/marketing/broadcast', { segment, message });
      toast.success(`Broadcast sent to ${data.sent} recipient(s)`);
      setMessage('');
      loadBroadcasts();
      loadSummary();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setSending(false); }
  };

  const triggerBirthdays = async () => {
    try {
      const { data } = await api.post('/marketing/birthday-rewards');
      toast.success(`${data.issued_count} birthday coupon(s) issued`);
      loadBroadcasts();
    } catch (e) { toast.error('Failed to issue birthday rewards'); }
  };

  return (
    <div className="min-h-screen bg-background">
      <StaffRail />
      <header className="flex items-center justify-between border-b border-border bg-card px-4 py-3 sm:px-6">
        <div>
          <h1 className="font-display text-lg font-semibold">Marketing</h1>
          <p className="text-xs text-muted-foreground">Segments &middot; Broadcasts &middot; Coupons &middot; Rewards</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => nav('/server')}>Server</Button>
          <Button size="sm" variant="ghost" onClick={() => { logout(); }}><LogOut className="mr-1 h-4 w-4" /> Sign out</Button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl space-y-4 px-4 py-6 sm:px-6">
        <Tabs defaultValue="broadcast">
          <TabsList>
            <TabsTrigger data-testid="marketing-tab-broadcast" value="broadcast">Broadcast</TabsTrigger>
            <TabsTrigger data-testid="marketing-tab-coupons" value="coupons">Coupons</TabsTrigger>
            <TabsTrigger data-testid="marketing-tab-birthday" value="birthday">Birthday rewards</TabsTrigger>
            <TabsTrigger data-testid="marketing-tab-history" value="history">History</TabsTrigger>
          </TabsList>

          {/* ===================== BROADCAST ===================== */}
          <TabsContent value="broadcast" className="space-y-4">
            {/* Segment chips with counts */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Pick a customer segment</CardTitle>
                <CardDescription className="text-xs">
                  Segments are computed live from the Customer directory. Click a chip to see who matches.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {SEGMENTS.map((s) => {
                    const Icon = s.icon;
                    const active = segment === s.key;
                    const n = counts[s.key] ?? '—';
                    return (
                      <button
                        key={s.key}
                        data-testid={`marketing-chip-${s.key}`}
                        type="button"
                        onClick={() => setSegment(s.key)}
                        className={`group inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition ${active ? 'border-primary bg-primary text-primary-foreground shadow-sm' : 'border-border bg-card hover:border-primary/40'}`}
                        title={s.hint}
                      >
                        <Icon className={`h-3.5 w-3.5 ${active ? '' : s.tone}`} />
                        <span>{s.label}</span>
                        <Badge variant="outline" className={`h-5 border-0 px-1.5 text-[10px] ${active ? 'bg-background/30 text-primary-foreground' : 'bg-secondary'}`}>{n}</Badge>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
                  {SEGMENTS.find((s) => s.key === segment)?.hint}
                </p>
              </CardContent>
            </Card>

            <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
              <Card>
                <CardHeader className="pb-2"><CardTitle>Compose SMS broadcast</CardTitle><CardDescription className="text-xs">Twilio is in PLACEHOLDER mode — messages are logged. Add real Twilio creds to send.</CardDescription></CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Message ({message.length}/320)</Label>
                    <Textarea data-testid="marketing-message-input" maxLength={320} rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Hi {name}! Tonight only — show this SMS for 15% off any pizza. Reply STOP to opt out." />
                  </div>
                  <Button data-testid="marketing-broadcast-button" onClick={broadcast} disabled={sending || message.length < 4 || !(preview?.count || 0)}>
                    <Send className="mr-1 h-4 w-4" /> {sending ? 'Sending…' : `Send to ${preview?.count || 0} recipient(s)`}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                  <div>
                    <CardTitle className="text-base">Recipients</CardTitle>
                    <CardDescription className="text-xs">Phones that match this segment</CardDescription>
                  </div>
                  <Button size="icon" variant="ghost" onClick={previewSeg} title="Refresh"><RefreshCw className="h-4 w-4" /></Button>
                </CardHeader>
                <CardContent>
                  <p data-testid="marketing-preview-count" className="font-display text-3xl font-semibold tabular text-primary">{preview?.count ?? 0}<span className="ml-2 text-sm text-muted-foreground">recipients</span></p>
                  <ul className="mt-3 max-h-72 space-y-1 overflow-auto text-xs">
                    {(preview?.sample || []).map((c) => (
                      <li key={c.phone} data-testid="marketing-preview-row" className="flex items-center justify-between rounded border border-border bg-card px-2 py-1">
                        <div className="min-w-0">
                          <p className="truncate font-medium">{c.name || '—'}</p>
                          <p className="font-mono text-muted-foreground">{c.phone}</p>
                        </div>
                        <div className="text-right text-[10px] text-muted-foreground">
                          {c.vip && <Badge className="ml-1 border-0 bg-amber-100 text-[9px] text-amber-900 hover:bg-amber-100"><Crown className="mr-0.5 h-2.5 w-2.5" />VIP</Badge>}
                          <p>{c.order_count} orders · {fmtMoney(c.total_spent || 0)}</p>
                        </div>
                      </li>
                    ))}
                    {!(preview?.sample || []).length && <li className="text-muted-foreground">No matches in this segment.</li>}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ===================== COUPONS ===================== */}
          <TabsContent value="coupons">
            <CouponsPanel />
          </TabsContent>

          {/* ===================== BIRTHDAY ===================== */}
          <TabsContent value="birthday">
            <Card>
              <CardHeader>
                <CardTitle><Cake className="mr-2 inline h-5 w-5 text-pink-600" /> Birthday rewards</CardTitle>
                <CardDescription>Issue a $10 fixed-amount coupon (valid 14 days) to every customer whose date of birth matches today. Idempotent per phone + day.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm">Today&apos;s birthdays: <strong data-testid="marketing-birthday-count">{counts.birthdays_today ?? 0}</strong> customer(s).</p>
                <p className="text-xs text-muted-foreground">Birthdays are read from the customer profile. Customers can set their DOB on signup; admins can edit it in the Customer directory.</p>
                <Button data-testid="marketing-birthday-trigger-button" onClick={triggerBirthdays} disabled={!counts.birthdays_today}><Cake className="mr-1 h-4 w-4" /> Issue birthday rewards now</Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===================== HISTORY ===================== */}
          <TabsContent value="history">
            <Card>
              <CardHeader><CardTitle>Recent broadcasts</CardTitle></CardHeader>
              <CardContent>
                {!broadcasts.length && <p className="text-sm text-muted-foreground">No broadcasts yet.</p>}
                <ul className="divide-y divide-border">
                  {broadcasts.map((b) => (
                    <li key={b.id} data-testid="marketing-history-row" className="py-3 text-sm">
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary">{b.segment}</Badge>
                        <span className="text-xs text-muted-foreground"><Users className="mr-1 inline h-3 w-3" /> {b.sent}/{b.recipients_count} &middot; {fmtTimeAgo(b.created_at)}</span>
                      </div>
                      <p className="mt-1"><MessageSquare className="mr-1 inline h-3.5 w-3.5 text-muted-foreground" /> {b.message}</p>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Coupons panel
// ---------------------------------------------------------------------------
function CouponsPanel() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // active | used | void | all
  const [confirmVoid, setConfirmVoid] = useState(null);

  const [draft, setDraft] = useState({
    type: 'percent',
    value: 10,
    code: '',
    max_redemptions: 1,
    min_order_subtotal: 0,
    expires_at: '',
    reason: 'manual',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/coupons');
      setCoupons(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load coupons');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return coupons.filter((c) => {
      if (filter === 'all') return true;
      if (filter === 'active') return c.status === 'active' && !c.is_expired;
      if (filter === 'used') return c.status === 'used';
      if (filter === 'void') return c.status === 'void' || c.is_expired;
      return true;
    });
  }, [coupons, filter]);

  const issue = async () => {
    try {
      const body = {
        type: draft.type,
        value: draft.type === 'free_delivery' ? 0 : Number(draft.value || 0),
        code: draft.code.trim().toUpperCase() || undefined,
        max_redemptions: Math.max(1, parseInt(draft.max_redemptions, 10) || 1),
        min_order_subtotal: Math.max(0, Number(draft.min_order_subtotal) || 0),
        expires_at: draft.expires_at || undefined,
        reason: draft.reason || 'manual',
      };
      await api.post('/coupons', body);
      toast.success('Coupon issued');
      setDraft({ ...draft, code: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to issue coupon');
    }
  };

  const doVoid = async () => {
    if (!confirmVoid) return;
    try {
      await api.post(`/coupons/${confirmVoid.code}/void`);
      toast.success(`${confirmVoid.code} voided.`);
      setConfirmVoid(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
      setConfirmVoid(null);
    }
  };

  const copyCode = (code) => {
    try {
      navigator.clipboard.writeText(code);
      toast.success(`Copied ${code}`);
    } catch (e) { /* */ }
  };

  const typeIcon = (t) => {
    if (t === 'percent') return <BadgePercent className="h-3 w-3" />;
    if (t === 'fixed') return <DollarSign className="h-3 w-3" />;
    if (t === 'free_delivery') return <Truck className="h-3 w-3" />;
    return null;
  };
  const typeLabel = (c) => {
    if (c.type === 'percent') return `${c.value}% off`;
    if (c.type === 'fixed') return `${fmtMoney(c.value)} off`;
    if (c.type === 'free_delivery') return 'Free delivery';
    return c.type;
  };

  return (
    <div className="grid gap-4 lg:grid-cols-5">
      {/* Issue new */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Plus className="h-4 w-4" /> Issue coupon</CardTitle>
          <CardDescription className="text-xs">
            Create a discount code. Share it via SMS or print on receipts. Codes are validated automatically when the customer (or cashier) types them at checkout.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Type</Label>
              <Select value={draft.type} onValueChange={(v) => setDraft((d) => ({ ...d, type: v }))}>
                <SelectTrigger data-testid="coupon-type-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent"><BadgePercent className="mr-1 inline h-3.5 w-3.5" /> Percentage off</SelectItem>
                  <SelectItem value="fixed"><DollarSign className="mr-1 inline h-3.5 w-3.5" /> Fixed amount off</SelectItem>
                  <SelectItem value="free_delivery"><Truck className="mr-1 inline h-3.5 w-3.5" /> Free delivery</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {draft.type !== 'free_delivery' && (
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {draft.type === 'percent' ? 'Percent (1–100)' : 'Amount off'}
                </Label>
                <Input
                  data-testid="coupon-value-input"
                  type="number"
                  min={draft.type === 'percent' ? 1 : 0.5}
                  max={draft.type === 'percent' ? 100 : undefined}
                  step="0.5"
                  className="mt-1"
                  value={draft.value}
                  onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))}
                />
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Code (optional)</Label>
              <Input
                data-testid="coupon-code-input"
                placeholder="Auto-generated if blank"
                className="mt-1 font-mono uppercase"
                value={draft.code}
                onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value.toUpperCase().slice(0, 12) }))}
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Max uses</Label>
              <Input
                data-testid="coupon-max-input"
                type="number"
                min={1}
                step={1}
                className="mt-1"
                value={draft.max_redemptions}
                onChange={(e) => setDraft((d) => ({ ...d, max_redemptions: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Min subtotal</Label>
              <Input
                data-testid="coupon-min-input"
                type="number"
                min={0}
                step="0.5"
                className="mt-1"
                value={draft.min_order_subtotal}
                onChange={(e) => setDraft((d) => ({ ...d, min_order_subtotal: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Expires</Label>
              <Input
                data-testid="coupon-expires-input"
                type="date"
                className="mt-1"
                value={draft.expires_at}
                onChange={(e) => setDraft((d) => ({ ...d, expires_at: e.target.value }))}
              />
            </div>
          </div>
          <Button data-testid="coupon-issue-button" onClick={issue}><Plus className="mr-1 h-4 w-4" /> Issue coupon</Button>
        </CardContent>
      </Card>

      {/* Coupon list */}
      <Card className="lg:col-span-3">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2 text-base"><Ticket className="h-4 w-4" /> Issued coupons</CardTitle>
            <CardDescription className="text-xs">Click a code to copy it.</CardDescription>
          </div>
          <Button size="icon" variant="ghost" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex flex-wrap gap-2">
            {['active', 'used', 'void', 'all'].map((f) => (
              <Button key={f} data-testid={`coupon-filter-${f}`} size="sm" variant={filter === f ? 'default' : 'secondary'} onClick={() => setFilter(f)} className="capitalize">{f}</Button>
            ))}
          </div>
          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!loading && filtered.length === 0 && <p className="rounded-md border border-dashed border-border py-6 text-center text-xs text-muted-foreground">No coupons in this filter.</p>}
          {!loading && filtered.length > 0 && (
            <div className="overflow-auto rounded-md border border-border">
              <table data-testid="coupon-list-table" className="w-full text-sm">
                <thead>
                  <tr className="bg-secondary/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2">Code</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2 text-center">Uses</th>
                    <th className="px-3 py-2">Reason</th>
                    <th className="px-3 py-2">Expires</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const isExpired = c.is_expired;
                    const isVoid = c.status === 'void';
                    const isUsed = c.status === 'used';
                    const bad = isExpired || isVoid;
                    return (
                      <tr key={c.code} data-testid="coupon-row" className={`border-t border-border ${bad ? 'opacity-60' : ''}`}>
                        <td className="px-3 py-2">
                          <button data-testid="coupon-code-copy" onClick={() => copyCode(c.code)} className="inline-flex items-center gap-1 font-mono text-sm font-semibold hover:text-primary" title="Copy">
                            {c.code} <Copy className="h-3 w-3 opacity-60" />
                          </button>
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="text-[10px]">
                            {typeIcon(c.type)} <span className="ml-1">{typeLabel(c)}</span>
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-center tabular">{c.redemption_count}/{c.max_redemptions}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground capitalize">{c.reason || '—'}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{c.expires_at ? c.expires_at.slice(0, 10) : 'never'}</td>
                        <td className="px-3 py-2">
                          {isVoid ? (
                            <Badge className="border-0 bg-rose-100 text-[10px] text-rose-800 hover:bg-rose-100"><Ban className="mr-1 h-3 w-3" />Void</Badge>
                          ) : isExpired ? (
                            <Badge className="border-0 bg-amber-100 text-[10px] text-amber-800 hover:bg-amber-100"><AlertCircle className="mr-1 h-3 w-3" />Expired</Badge>
                          ) : isUsed ? (
                            <Badge className="border-0 bg-slate-200 text-[10px] text-slate-700 hover:bg-slate-200">Used</Badge>
                          ) : (
                            <Badge className="border-0 bg-emerald-100 text-[10px] text-emerald-800 hover:bg-emerald-100"><CheckCircle2 className="mr-1 h-3 w-3" />Active</Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {!isVoid && !isUsed && (
                            <Button data-testid="coupon-void-button" size="sm" variant="ghost" className="h-7 px-2 text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => setConfirmVoid(c)}>
                              <Ban className="mr-1 h-3 w-3" /> Void
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmVoid} onOpenChange={(o) => { if (!o) setConfirmVoid(null); }}>
        <AlertDialogContent data-testid="coupon-void-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Void {confirmVoid?.code}?</AlertDialogTitle>
            <AlertDialogDescription>
              The coupon will no longer be usable. Customers attempting to redeem it will be shown an error. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Back</AlertDialogCancel>
            <AlertDialogAction onClick={doVoid} className="bg-rose-600 text-white hover:bg-rose-700">Void coupon</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
